/**
 * src/app/(public)/transparencia/page.jsx
 *
 * Vista pública del audit log de cambios sistémicos. Demuestra trust:
 * cualquier cambio relevante en governanza, pricing, incidentes,
 * deploys queda registrado y visible.
 */

'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Gavel, Info, Warning, XCircle, ClockClockwise } from '@phosphor-icons/react/dist/ssr';

const SEVERITY_COLORS = {
  info: { bg: 'rgba(59, 130, 246, 0.06)', border: 'rgba(59, 130, 246, 0.2)', text: '#3B82F6', icon: Info },
  warning: { bg: 'rgba(245, 158, 11, 0.08)', border: 'rgba(245, 158, 11, 0.3)', text: '#F59E0B', icon: Warning },
  critical: { bg: 'rgba(220, 38, 38, 0.08)', border: 'rgba(220, 38, 38, 0.3)', text: '#dc2626', icon: XCircle },
};

export default function TransparenciaPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/admin/audit-public?limit=50');
        if (res.ok) {
          const data = await res.json();
          setEvents(data.events || []);
        }
      } catch {}
      setLoading(false);
    })();
  }, []);

  return (
    <div style={pageStyle}>
      <header style={headerStyle}>
        <Link href="/" style={brandStyle}>
          <Gavel size={20} weight="duotone" color="#B8860B" />
          <span>Dr. Víctor</span>
        </Link>
      </header>

      <main style={mainStyle}>
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={titleStyle}>Transparency Report</h1>
          <p style={subStyle}>
            Registro público de cambios sistémicos: cuotas, pricing, incidentes, deploys.
            Cualquier cosa que afecte cómo Dr. Víctor opera para todos los usuarios queda
            documentada aquí. Sin secretos, sin truco.
          </p>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
            <ClockClockwise size={28} weight="duotone" />
            <div>Cargando registro…</div>
          </div>
        ) : events.length === 0 ? (
          <div style={{ ...cardStyle, textAlign: 'center', color: '#94a3b8' }}>
            <p>Aún no hay eventos públicos registrados. Cualquier cambio futuro aparecerá aquí.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {events.map(e => {
              const sev = SEVERITY_COLORS[e.severity] || SEVERITY_COLORS.info;
              const SeverityIcon = sev.icon;
              return (
                <article key={e.id} style={{
                  ...cardStyle,
                  background: sev.bg,
                  borderColor: sev.border,
                }}>
                  <header style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', marginBottom: '0.5rem' }}>
                    <SeverityIcon size={18} weight="bold" color={sev.text} style={{ flexShrink: 0, marginTop: 2 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={{ margin: 0, fontSize: '1rem', color: '#0F172A', fontWeight: 700 }}>{e.title}</h3>
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 2 }}>
                        <span style={{ color: sev.text, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{e.event_type}</span>
                        {' · '}
                        {new Date(e.occurred_at).toLocaleString('es-DO', { dateStyle: 'long', timeStyle: 'short' })}
                      </div>
                    </div>
                  </header>
                  {e.description && (
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569', lineHeight: 1.55, paddingLeft: '2rem' }}>
                      {e.description}
                    </p>
                  )}
                  {e.metadata && Object.keys(e.metadata).length > 0 && (
                    <details style={{ paddingLeft: '2rem', marginTop: '0.5rem' }}>
                      <summary style={{ cursor: 'pointer', fontSize: '0.72rem', color: '#94a3b8' }}>Detalles técnicos</summary>
                      <pre style={metaStyle}>{JSON.stringify(e.metadata, null, 2)}</pre>
                    </details>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </main>

      <footer style={footerStyle}>
        <Link href="/" style={{ color: '#B8860B', textDecoration: 'none', fontWeight: 600 }}>← Volver al inicio</Link>
      </footer>
    </div>
  );
}

const pageStyle = { minHeight: '100vh', background: 'linear-gradient(180deg, #fdfaf2 0%, #ffffff 200px)', fontFamily: 'var(--font-manrope), -apple-system, sans-serif' };
const headerStyle = { padding: '1.25rem 2rem', borderBottom: '1px solid rgba(184, 134, 11, 0.1)' };
const brandStyle = { display: 'inline-flex', alignItems: 'center', gap: '0.5rem', color: '#B8860B', textDecoration: 'none', fontWeight: 700 };
const mainStyle = { maxWidth: 800, margin: '2rem auto', padding: '0 1.5rem' };
const titleStyle = { fontSize: '1.85rem', fontWeight: 800, color: '#0F172A', margin: 0, letterSpacing: '-0.02em' };
const subStyle = { color: '#57534e', margin: '0.5rem 0 0', fontSize: '0.92rem', lineHeight: 1.55 };
const cardStyle = { padding: '1.1rem 1.25rem', background: '#fff', border: '1px solid rgba(184, 134, 11, 0.1)', borderRadius: 12 };
const metaStyle = { background: '#fcfcfa', padding: '0.6rem 0.75rem', borderRadius: 6, fontSize: '0.72rem', overflowX: 'auto', marginTop: '0.4rem' };
const footerStyle = { padding: '2rem', textAlign: 'center', borderTop: '1px solid rgba(184, 134, 11, 0.08)', marginTop: '2rem' };
